import React, { useState } from 'react';
import { Divider, Grid, Paper, Stack } from '@esgian/esgianui';
import { PortsTable } from '@components/Tables';
import YtdPortCallStatsTable from '@components/Tables/YtdPortCallStatsTable';
import moment from 'moment';
import { GlobalAveragePortTimeChart } from '@components/Charts';
import {
  getActivePorts,
  getGlobalPortsStats,
  getPortCallSummary,
  getPortsDetails,
  getTopPorts
} from '@api';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType, getSegment } from '@store/features';
import { TextWithTooltipIcon } from '@components';
import { GLOBAL_AVERAGE_PORT_TIME_TEXT } from '@constants/tooltips';
import { DateRangePicker } from '@components/Inputs';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { DATE_FORMAT, GEOGRAPHICAL_TYPES, TIMESTAMP } from '@constants';

function PortsOverviewSection() {
  const segment = useSelector(getSegment);
  const operatorType = useSelector(getOperatorType);
  const commercialCategoryType = useSelector(getCommercialCategoryType);

  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [startDate, setStartDate] = useState(moment().subtract(11, 'months').startOf('month'));
  const [endDate, setEndDate] = useState(moment());

  const portStatsQuery = useQuery({
    queryKey: [
      'portStats',
      {
        OperatorTypeId: operatorType,
        SegmentTypeId: segment.id,
        StartDate: moment.utc(startDate.format(TIMESTAMP)).toISOString(),
        EndDate: moment.utc(endDate.format(TIMESTAMP)).toISOString(),
        CommercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!startDate && !!endDate,
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return getGlobalPortsStats(
        {
          ...queryKey[1]
        },
        signal
      )
        .then((result) => {
          return result;
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            toast.error(`Unable to fetch port stats data`, {
              position: 'top-right'
            });
          }
          return null;
        });
    }
  });

  const topPortsQuery = useQuery({
    queryKey: [
      'topPorts',
      {
        NumberOfPorts: 100,
        NumberOfOperators: 3,
        Year: selectedYear,
        OperatorTypeId: operatorType,
        SegmentTypeId: segment.id,
        CommercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!selectedYear,
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return getTopPorts(queryKey[1], signal)
        .then((result) => {
          const { topPortsYTD } = result;
          return topPortsYTD;
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            toast.error(`Unable to fetch top ports`, {
              position: 'top-right'
            });
          }
          return null;
        });
    }
  });

  const activePortsQuery = useQuery({
    queryKey: [
      'activePorts',
      {
        geoType: GEOGRAPHICAL_TYPES.PORT,
        SegmentTypeId: segment.id,
        operatorTypeId: operatorType,
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!segment.id,
    placeholderData: null,
    queryFn: async ({ queryKey, signal }) => {
      const queryParams = queryKey[1];

      try {
        const [ports, portCall] = await Promise.all([
          getActivePorts(queryParams, signal),
          getPortCallSummary(queryParams, signal)
        ]);
        let details = await getPortsDetails(signal, {
          region: false,
          terminals: false,
          ids: ports.map(({ geoId }) => geoId)
        }).then((resp) => {
          let res = {};
          resp.forEach((item) => {
            res[item.id] = item;
          });
          return res;
        });

        return ports.map((port) => {
          let portCallData = portCall[port.geoId] ?? {};
          let detailsData = details[port.geoId]?.country ?? {};

          return { ...port, ...portCallData, country: detailsData };
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error(`Unable to fetch active ports`, {
            position: 'top-right'
          });
        }
        return null;
      }
    }
  });
  function isSelectedMonthCurrent(selectedDate) {
    return moment(selectedDate).isSame(moment(), 'month');
  }
  return (
    <Grid container spacing={2}>
      <Grid item lg={6} sm={12}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Stack spacing={2}>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <TextWithTooltipIcon
                label={'Global Average Port Time'}
                labelVariant={'h6'}
                tooltipText={GLOBAL_AVERAGE_PORT_TIME_TEXT}
              />
              <DateRangePicker
                inputSize={'small'}
                label={'Period'}
                isRequired
                startDate={startDate}
                endDate={endDate}
                onClose={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
              />
            </Stack>
            <Divider />
            <GlobalAveragePortTimeChart
              startDate={startDate.format(DATE_FORMAT)}
              endDate={endDate.format(DATE_FORMAT)}
              globalPortStats={portStatsQuery.data}
              loading={portStatsQuery.isFetching}
              headerText={'Global Average Port Time'}
              isEndDateCurrentMonth={isSelectedMonthCurrent(endDate)}
            />
          </Stack>
        </Paper>
      </Grid>
      <Grid item lg={6} sm={12}>
        <YtdPortCallStatsTable
          handleSelectYear={({ target }) => setSelectedYear(target.value)}
          selectedYear={selectedYear}
          topPortsData={topPortsQuery.data}
          loading={topPortsQuery.isFetching}
        />
      </Grid>
      <Grid item xs={12}>
        <PortsTable ports={activePortsQuery.data} loading={activePortsQuery.isFetching} />
      </Grid>
    </Grid>
  );
}

PortsOverviewSection.propTypes = {};

export default PortsOverviewSection;
